import { useCallback, useState } from 'react';
import NProgress from 'nprogress';

// ** Types
import type { AuthUser } from '@erikdakoda/auth/types';

// ** Hooks
import { useCurrentUser } from './useCurrentUser';
import { useSnack } from './useSnack';
import { useException } from './useException';
import { useUpdateUser as useUpdateUserMutation } from '@erikdakoda/database/hooks/user';
import type { UserUpdateArgs } from '@erikdakoda/database/types';

export function useUpdateUserFields() {
  const { user, updateCurrentUser, refetchCurrentUser } = useCurrentUser();
  const { snack } = useSnack();
  const exception = useException();
  const updateUser = useUpdateUserMutation().mutateAsync;
  const [isLoading, setIsLoading] = useState(false);

  const updateUserFields = useCallback(
    async (data: Partial<AuthUser>, successMessage: string = 'The user was updated') => {
      setIsLoading(true);
      NProgress.start();
      try {
        await updateUser({ where: { id: user?.id }, data: data as UserUpdateArgs['data'] });
        if (!('phoneNumber' in data)) {
          await updateCurrentUser(data);
        } else {
          await refetchCurrentUser();
        }
        snack.success(successMessage);
      } catch (err: unknown) {
        exception(err);
      } finally {
        NProgress.done();
        setIsLoading(false);
      }
    },
    [user, updateUser, updateCurrentUser, refetchCurrentUser, snack, exception],
  );

  return { updateUserFields, isLoading };
}
